import type {
  AchievementResult,
  AchievementView,
  EventsAnswer,
  SaveInfo,
  ScoreResult,
} from '@mogzo/shared';
import {
  achievementProgress,
  deleteSave,
  getConfig,
  getEvents,
  getSave,
  listAchievements,
  listSaves,
  type PlayAuth,
  PlayError,
  setSave,
  submitScore,
  unlockAchievement,
} from './play';

/**
 * Fewer calls per player (M24b), so the same server holds more players. The game's calls look
 * the same to it; what reaches the API changes:
 * - A save's first write this session goes at once (so a full quota is reported then); later
 *   writes of it go at most once a minute, the newest value only, and at once when the game
 *   pauses or the page is hidden or closed. `save.get` answers with what the page already knows.
 * - A repeat unlock, or progress at or below what the player has, is answered on the page.
 * - Progress that doesn't unlock goes at most once a minute; progress that unlocks goes at once.
 * - The game's config, and its live events (M37), are reused for 5 minutes, or until an event
 *   next turns on or off.
 * - A score that can't beat the player's best on its board (M36) is answered on the page with
 *   the best and rank last heard, for 5 minutes (or until an event turns on or off); most runs
 *   aren't a new best. Not while a live event counts the board's scores on a board of its own:
 *   a score there may be the player's best on that board.
 */

export const FLUSH_AFTER_MS = 60_000;
export const CONFIG_FOR_MS = 5 * 60_000;
export const SCORES_FOR_MS = 5 * 60_000;

type WithToken = <T>(call: (auth: PlayAuth) => Promise<T>) => Promise<T>;

/** A delayed write that failed for a reason trying again won't fix (a 4xx other than 429). */
const isFinal = (error: unknown) =>
  error instanceof PlayError && error.status < 500 && error.status !== 429;

export function createPlayCache(
  withToken: WithToken,
  {
    flushAfterMs = FLUSH_AFTER_MS,
    now = Date.now,
    onUnlock,
  }: {
    flushAfterMs?: number;
    now?: () => number;
    /** Progress sent later can still unlock (the player got further in another tab). */
    onUnlock?: (achievement: AchievementView) => void;
  } = {},
) {
  /** Each save's newest value this page knows of: read, sent or waiting to be sent. */
  const saves = new Map<string, unknown>();
  /** Saves written this session at least once, so later writes can wait. */
  const written = new Set<string>();
  const waitingSaves = new Map<string, unknown>();
  const achievements = new Map<string, AchievementView>();
  const waitingProgress = new Map<string, number>();
  /** Why a delayed save was refused (too big, say), told to the game on its next save. */
  let delayedError: PlayError | null = null;
  let config: { value: unknown; until: number } | null = null;
  let events: { value: EventsAnswer; until: number } | null = null;
  /** Kept for `forMs`, but no longer than until the schedule changes. */
  const keepUntil = (forMs: number, changesAt: string | null | undefined) => {
    const change = changesAt ? Date.parse(changesAt) : Number.NaN;
    return Number.isNaN(change) ? now() + forMs : Math.min(now() + forMs, change);
  };
  /** Each board's answer to the last score sent: the best, the rank and the board's order. */
  const scores = new Map<string, { result: ScoreResult; until: number }>();
  let timer: ReturnType<typeof setTimeout> | null = null;
  let flushing: Promise<void> | null = null;

  function later() {
    timer ??= setTimeout(() => {
      timer = null;
      void flush();
    }, flushAfterMs);
  }

  /** Learns an achievement's state, never going back (a slower answer can't undo an unlock). */
  function learn(view: AchievementView) {
    const known = achievements.get(view.id);
    if (known?.unlocked && !view.unlocked) return;
    if (known && !view.unlocked && known.progress > view.progress) return;
    achievements.set(view.id, view);
  }

  function learned(result: AchievementResult) {
    learn(result.achievement);
    return result;
  }

  /** Sends everything waiting. `keepalive` lets the requests finish after the page closes. */
  function flush({ keepalive = false }: { keepalive?: boolean } = {}): Promise<void> {
    if (timer) clearTimeout(timer);
    timer = null;
    const run = async () => {
      const saveBatch = [...waitingSaves];
      const progressBatch = [...waitingProgress];
      waitingSaves.clear();
      waitingProgress.clear();
      await Promise.all([
        ...saveBatch.map(async ([key, value]) => {
          try {
            await withToken((auth) => setSave(auth, key, value, { keepalive }));
          } catch (error) {
            // Tried again next time, unless the game wrote something newer meanwhile; a reason
            // trying again won't fix goes to the game on its next save.
            if (isFinal(error)) delayedError = error as PlayError;
            else if (!waitingSaves.has(key)) {
              waitingSaves.set(key, value);
              later();
            }
          }
        }),
        ...progressBatch.map(async ([id, value]) => {
          try {
            const result = learned(
              await withToken((auth) => achievementProgress(auth, id, value, { keepalive })),
            );
            if (result.newlyUnlocked) onUnlock?.(result.achievement);
          } catch (error) {
            if (!isFinal(error)) {
              waitingProgress.set(id, Math.max(value, waitingProgress.get(id) ?? 0));
              later();
            }
          }
        }),
      ]);
    };
    // One flush at a time, so the order of a save's writes is kept.
    const next = (flushing ?? Promise.resolve()).then(run, run);
    flushing = next;
    void next.finally(() => {
      if (flushing === next) flushing = null;
    });
    return next;
  }

  return {
    flush,
    /** True while something waits to be sent (tests). */
    waiting: () => waitingSaves.size + waitingProgress.size > 0,

    async getSave(key: string): Promise<unknown> {
      if (saves.has(key)) return saves.get(key);
      const value = await withToken((auth) => getSave(auth, key));
      // Unless the game wrote it while this read was on its way.
      if (!saves.has(key)) saves.set(key, value);
      return saves.get(key);
    },

    async setSave(key: string, value: unknown): Promise<void> {
      if (delayedError) {
        const error = delayedError;
        delayedError = null;
        throw error;
      }
      if (!written.has(key)) {
        written.add(key);
        try {
          await withToken((auth) => setSave(auth, key, value));
        } catch (error) {
          written.delete(key);
          throw error;
        }
        saves.set(key, value);
        return;
      }
      saves.set(key, value);
      waitingSaves.set(key, value);
      later();
    },

    async deleteSave(key: string): Promise<boolean> {
      waitingSaves.delete(key);
      // Its next write counts against the quota again, so it goes at once.
      written.delete(key);
      const deleted = await withToken((auth) => deleteSave(auth, key));
      saves.set(key, null);
      return deleted;
    },

    async listSaves(): Promise<SaveInfo[]> {
      await flush();
      return withToken(listSaves);
    },

    async listAchievements(): Promise<AchievementView[]> {
      await flush();
      const list = await withToken(listAchievements);
      for (const view of list) learn(view);
      return list;
    },

    async unlock(id: string): Promise<AchievementResult> {
      const known = achievements.get(id);
      if (known?.unlocked) return { achievement: known, newlyUnlocked: false };
      return learned(await withToken((auth) => unlockAchievement(auth, id)));
    },

    async progress(id: string, value: number): Promise<AchievementResult> {
      const known = achievements.get(id);
      // Not known yet (its steps among them), or one without steps: the API answers.
      if (!known || known.steps === null) {
        return learned(await withToken((auth) => achievementProgress(auth, id, value)));
      }
      if (known.unlocked || value <= known.progress) {
        return { achievement: known, newlyUnlocked: false };
      }
      if (value >= known.steps) {
        waitingProgress.delete(id);
        return learned(await withToken((auth) => achievementProgress(auth, id, value)));
      }
      const ahead = { ...known, progress: value };
      achievements.set(id, ahead);
      waitingProgress.set(id, value);
      later();
      return { achievement: ahead, newlyUnlocked: false };
    },

    async submitScore(board: string, score: number): Promise<ScoreResult> {
      const known = scores.get(board);
      if (known && known.until > now()) {
        const { best, sort } = known.result;
        // Ties keep the earlier score, as the API does.
        if (sort === 'desc' ? score <= best : score >= best) {
          return { ...known.result, improved: false };
        }
      }
      const result = await withToken((auth) => submitScore(auth, board, score));
      if (result.alsoOn?.length) scores.delete(board);
      else scores.set(board, { result, until: keepUntil(SCORES_FOR_MS, result.changesAt) });
      return result;
    },

    async getConfig(): Promise<unknown> {
      if (config && config.until > now()) return config.value;
      const answer = await withToken(getConfig);
      config = { value: answer.config, until: keepUntil(CONFIG_FOR_MS, answer.changesAt) };
      return answer.config;
    },

    /** What's on, and when it next changes (the SDK reads it again then). */
    async getEvents(): Promise<EventsAnswer> {
      if (events && events.until > now()) return events.value;
      const value = await withToken(getEvents);
      events = { value, until: keepUntil(CONFIG_FOR_MS, value.changesAt) };
      return value;
    },
  };
}

export type PlayCache = ReturnType<typeof createPlayCache>;
